import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

import { TraceabilityReportPageInfo } from '@qality/common-components/src/TraceabilityOnboarding/TraceabilityReportPageInfo';
import TraceabilityFeedback from '@qality/common-components/src/feedback-popup/TraceabilityFeedback';
import { Report } from '@qality/common-model';

import TestExecutionReportService from './TestExecutionReportService';
import TraceabilityReportService from './TraceabilityReportService';

export default {
  reports: {
    TestExecutionReport: new Report.Builder()
      .setName('Test Execution Report')
      .setDescription(
        'A report that summarizes tests that were executed in the selected period.'
      )
      .setFetchFunction(TestExecutionReportService.getData)
      .implementGetColumnNames(() => {
        return [
          {
            key: 'test_case',
            name: 'Test Case',
            type: 'ISSUE',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
          {
            key: 'test_cycle',
            name: 'Test Cycle',
            type: 'TEST_CYCLE',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
          {
            key: 'created_on',
            name: 'Created On',
            type: 'DATE',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
          {
            key: 'execution_assignee',
            name: 'Execution Assignee',
            type: 'USER_WITH_AVATAR',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
          {
            key: 'test_result',
            name: 'Test Result',
            type: 'EXECUTION_STATUS',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
          {
            key: 'actions',
            name: 'Actions',
            type: 'RELATIVE_LINK',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
        ];
      })
      .setFilters({
        projectSelect: {
          isClearable: true,
          useAllOption: true,
        },
        testCycleSelect: true,
        userSelect: true,
        statusSelect: true,
        dateRangeSelect: true,
        testTypeSelect: true,
      })
      .setShowChartDropdown(true)
      .setRowAction(true)
      .build(),

    RecentlyExecutedTestCases: new Report.Builder()
      .setName('')
      .setDescription('')
      .setFetchFunction(TestExecutionReportService.getData)
      .implementGetColumnNames(() => {
        return [
          {
            key: 'test_case',
            name: 'Test Case',
            type: 'ISSUE',
            width: 'auto',
            minWidth: '120px',
            maxWidth: 'none',
          },
          {
            key: 'test_cycle',
            name: 'Test Cycle',
            type: 'TEST_CYCLE',
            width: '30%',
            minWidth: '100px',
            maxWidth: 'none',
          },
          {
            key: 'test_result',
            name: 'Test Result',
            type: 'EXECUTION_STATUS',
            width: 'auto',
            minWidth: '95px',
            maxWidth: 'none',
          },
          {
            key: 'created_on',
            name: 'Date',
            type: 'DATE',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
        ];
      })
      .setPaginationDisabled(true)
      .setFilters(null)
      .setStyles({
        cellPadding: 10,
      })
      .build(),

    TraceabilityReport: new Report.Builder()
      .setName('Traceability Report')
      .setDescription(
        'This report summarizes bugs associated with test cases and their executions in the selected period.'
      )
      .setFetchFunction(TraceabilityReportService.getData)
      .setFilters({
        projectSelect: {
          isClearable: false,
          useAllOption: false,
        },
        dateRangeSelect: true,
        requirementSelect: true,
        searchSelect: true,
        moreFiltersSelect: true,
        testTypeSelect: true,
      })
      .setGetDefaultDates(() => {
        const startDate = moment()
          .subtract(3, 'months')
          .add(1, 'day')
          .format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
      })
      .implementGetColumnNames((filters) => {
        if (
          filters.requirementTypeName &&
          filters.requirementTypeName !== 'Skip requirements'
        ) {
          return [
            {
              key: 'requirement',
              name: 'Requirement',
              type: 'ISSUE',
              width: '30%',
              minWidth: '120px',
              maxWidth: 'none',
            },
            {
              key: 'test_case',
              name: 'Test Case',
              type: 'ISSUE',
              width: '25%',
              minWidth: '120px',
              maxWidth: 'none',
            },
            {
              key: 'test_executions',
              name: 'Test Executions',
              type: 'TEST_EXECUTIONS',
              width: 'auto',
              minWidth: '0',
              maxWidth: 'none',
            },
            {
              key: 'bugs',
              name: 'Bugs',
              type: 'BUG_IDS',
              width: '25%',
              minWidth: '0',
              maxWidth: 'none',
            },
          ];
        }
        return [
          {
            key: 'test_case',
            name: 'Test Case',
            type: 'ISSUE',
            width: '40%',
            minWidth: '120px',
            maxWidth: 'none',
          },
          {
            key: 'test_executions',
            name: 'Test Executions',
            type: 'TEST_EXECUTIONS',
            width: 'auto',
            minWidth: '0',
            maxWidth: 'none',
          },
          {
            key: 'bugs',
            name: 'Bugs',
            type: 'BUG_IDS',
            width: '40%',
            minWidth: '0',
            maxWidth: 'none',
          },
        ];
      })
      .setShowChartDropdown(false)
      .setModals([TraceabilityReportPageInfo, TraceabilityFeedback])
      .setRetrieveFiltersWithDefaultValues((originalFilters) => {
        const filters = cloneDeep(originalFilters);
        if (filters.onlyActiveTestCases === undefined) {
          filters.onlyActiveTestCases = false;
        }
        return filters;
      })
      .build(),
  },

  getReportByName(name: string) {
    return this.reports[name];
  },
};
